import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Landmark, Wallet } from "lucide-react";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import { printBalanceSheet } from "@/lib/balanceSheetPrint";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const today = () => new Date().toISOString().slice(0, 10);

function SectionBlock({ title, sections, icon: Icon, tone, onRowClick }) {
  if (!sections?.length) {
    return (
      <Card className="p-5">
        <SectionTitle className="!mb-2">{title}</SectionTitle>
        <p className="text-sm text-slate-500">No balances on this date.</p>
      </Card>
    );
  }

  return (
    <ReportTableCard
      title={title}
      dense={false}
      actions={
        <Icon size={16} className={tone === "blue" ? "text-blue-600" : "text-violet-600"} />
      }
      className="mb-0"
    >
      <div className="divide-y">
        {sections.map((section) => (
          <div key={section.schedule_id ?? section.schedule_name} className="px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-semibold text-slate-800">{section.schedule_name}</h3>
              <Pill tone={tone}>{aed(section.subtotal)}</Pill>
            </div>
            <div className="report-table">
              <table>
                <tbody>
                  {section.accounts.map((a) => (
                    <tr
                      key={a.account_id}
                      className="cursor-pointer"
                      onClick={() => onRowClick(a.account_id)}
                    >
                      <td className="font-mono text-blue-700 w-[14%]">{a.code}</td>
                      <td className="text-slate-700">{a.name}</td>
                      <td className="text-right font-mono font-medium w-[18%]">{aed(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </ReportTableCard>
  );
}

export default function BalanceSheetReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [asOfDate, setAsOfDate] = useState(today);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["balance-sheet", branchId, asOfDate],
    queryFn: async () => {
      const { data: res } = await api.get("/balance-sheet", {
        params: { ...reportParams, as_of_date: asOfDate },
      });
      return res;
    },
    enabled: Boolean(canQuery && asOfDate),
  });

  const openLedger = (accountId) => {
    const yearStart = `${asOfDate.slice(0, 4)}-01-01`;
    navigate(`/accounts/ledger?account_id=${accountId}&from_date=${yearStart}&to_date=${asOfDate}`);
  };

  return (
    <ReportPageWrap>
      <PageHeader
        title="Balance Sheet"
        subtitle="Assets and liabilities as at the selected date"
        actions={
          <ReportExportButtons
            disabled={!report}
            title="Balance Sheet"
            filename="balance-sheet"
            printTestId="bs-print"
            onPrint={() => printBalanceSheet(report, "Balance Sheet", { branchId, allBranches })}
          />
        }
      />

      <Card className="p-5 mb-4 max-w-sm">
        <SectionTitle className="!mb-3">As At Date</SectionTitle>
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Date</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="h-9"
            data-testid="bs-date"
          />
        </div>
      </Card>

      {report && (
        <ReportKpiStrip
          items={[
            {
              label: "Total Assets",
              value: <span data-testid="bs-assets">{aed(report.total_assets)}</span>,
              emphasize: true,
            },
            {
              label: "Total Liabilities",
              value: <span data-testid="bs-liabilities">{aed(report.total_liabilities)}</span>,
            },
            {
              label: "Status",
              value: (
                <span data-testid="bs-status">
                  {report.is_balanced ? "Balanced" : `Diff ${aed(report.difference)}`}
                </span>
              ),
            },
          ]}
        />
      )}

      {(isLoading || isFetching) && (
        <div className="flex items-center justify-center py-10 text-slate-500 text-sm mb-4">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading report…
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SectionBlock
          title="Assets"
          sections={report?.asset_sections}
          icon={Wallet}
          tone="blue"
          onRowClick={openLedger}
        />
        <SectionBlock
          title="Liabilities & Capital"
          sections={report?.liability_sections}
          icon={Landmark}
          tone="purple"
          onRowClick={openLedger}
        />
      </div>
    </ReportPageWrap>
  );
}
